import { 
  collection, 
  addDoc,
  getDocs,
  query, 
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'auditLogs';

/**
 * Remove campos undefined de um objeto recursivamente
 * Firebase não aceita valores undefined
 */
const removeUndefined = (obj) => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (typeof obj !== 'object') {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeUndefined(item)).filter(item => item !== undefined);
  }
  
  const cleaned = {};
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== undefined) {
      if (value === null || typeof value !== 'object') {
        cleaned[key] = value;
      } else {
        const cleanedValue = removeUndefined(value);
        if (cleanedValue !== null && Object.keys(cleanedValue).length > 0) {
          cleaned[key] = cleanedValue;
        }
      }
    }
  });
  
  return cleaned;
};

/**
 * Tipos de ações de auditoria
 */
export const AUDIT_ACTIONS = {
  // Autenticação
  REGISTER: 'register',
  REGISTER_RECOVERED: 'register_recovered',
  LOGIN: 'login',
  LOGIN_GOOGLE: 'login_google',
  LOGOUT: 'logout',
  PASSWORD_RESET_REQUEST: 'password_reset_request',

  // Barbearias
  CREATE_BARBERSHOP: 'create_barbershop',
  UPDATE_BARBERSHOP: 'update_barbershop',
  DELETE_BARBERSHOP: 'delete_barbershop',
  
  // Usuários
  CREATE_USER: 'create_user',
  UPDATE_USER: 'update_user',
  DELETE_USER: 'delete_user',
  CHANGE_USER_ROLE: 'change_user_role',
  
  // Agendamentos
  CREATE_APPOINTMENT: 'create_appointment',
  UPDATE_APPOINTMENT: 'update_appointment',
  CANCEL_APPOINTMENT: 'cancel_appointment',
  CONFIRM_APPOINTMENT: 'confirm_appointment',
  COMPLETE_APPOINTMENT: 'complete_appointment',
  NO_SHOW_APPOINTMENT: 'no_show_appointment',
  
  // Horários
  CREATE_SLOT: 'create_slot',
  CREATE_BULK_SLOTS: 'create_bulk_slots',
  DELETE_SLOT: 'delete_slot',
  
  // Serviços
  UPDATE_SERVICES: 'update_services',
  
  // Avaliações
  CREATE_REVIEW: 'create_review',
  DELETE_REVIEW: 'delete_review',
};

export const AUDIT_ACTION_LABELS = {
  [AUDIT_ACTIONS.REGISTER]: 'Cadastro de usuário',
  [AUDIT_ACTIONS.REGISTER_RECOVERED]: 'Cadastro (histórico recuperado)',
  [AUDIT_ACTIONS.LOGIN]: 'Login com e-mail',
  [AUDIT_ACTIONS.LOGIN_GOOGLE]: 'Login com Google',
  [AUDIT_ACTIONS.LOGOUT]: 'Logout',
  [AUDIT_ACTIONS.PASSWORD_RESET_REQUEST]: 'Solicitou recuperação de senha',
  [AUDIT_ACTIONS.CREATE_BARBERSHOP]: 'Criou barbearia',
  [AUDIT_ACTIONS.UPDATE_BARBERSHOP]: 'Atualizou barbearia',
  [AUDIT_ACTIONS.DELETE_BARBERSHOP]: 'Deletou barbearia',
  [AUDIT_ACTIONS.CREATE_USER]: 'Criou usuário',
  [AUDIT_ACTIONS.UPDATE_USER]: 'Atualizou usuário',
  [AUDIT_ACTIONS.DELETE_USER]: 'Deletou usuário',
  [AUDIT_ACTIONS.CHANGE_USER_ROLE]: 'Alterou cargo de usuário',
  [AUDIT_ACTIONS.CREATE_APPOINTMENT]: 'Criou agendamento',
  [AUDIT_ACTIONS.UPDATE_APPOINTMENT]: 'Atualizou agendamento',
  [AUDIT_ACTIONS.CANCEL_APPOINTMENT]: 'Cancelou agendamento',
  [AUDIT_ACTIONS.CONFIRM_APPOINTMENT]: 'Confirmou agendamento',
  [AUDIT_ACTIONS.COMPLETE_APPOINTMENT]: 'Concluiu agendamento',
  [AUDIT_ACTIONS.NO_SHOW_APPOINTMENT]: 'Marcou como não compareceu',
  [AUDIT_ACTIONS.CREATE_SLOT]: 'Criou horário',
  [AUDIT_ACTIONS.CREATE_BULK_SLOTS]: 'Criou horários em lote',
  [AUDIT_ACTIONS.DELETE_SLOT]: 'Deletou horário',
  [AUDIT_ACTIONS.UPDATE_SERVICES]: 'Atualizou serviços',
  [AUDIT_ACTIONS.CREATE_REVIEW]: 'Criou avaliação',
  [AUDIT_ACTIONS.DELETE_REVIEW]: 'Deletou avaliação',
};

/**
 * Registrar log de auditoria
 * @param {Object} logData - Dados do log
 * @param {string} logData.action - Tipo de ação (use AUDIT_ACTIONS)
 * @param {string} logData.userId - ID do usuário que executou a ação
 * @param {string} logData.userName - Nome do usuário
 * @param {string} logData.userRole - Cargo do usuário (admin, owner, user)
 * @param {string} logData.targetType - Tipo de entidade afetada (barbershop, user, appointment, etc)
 * @param {string} logData.targetId - ID da entidade afetada
 * @param {string} logData.targetName - Nome da entidade afetada
 * @param {Object} logData.changes - Objeto com as mudanças (before/after)
 * @param {string} logData.description - Descrição da ação
 * @param {Object} logData.metadata - Dados adicionais
 */
export const createAuditLog = async (logData) => {
  try {
    // Validar campos obrigatórios
    if (!logData.userId || !logData.action) {
      console.warn('⚠️ Audit log ignorado: userId ou action não fornecido', logData);
      return { success: true, skipped: true };
    }

    const log = {
      action: logData.action,
      userId: logData.userId,
      userName: logData.userName || 'Desconhecido',
      userEmail: logData.userEmail || null,
      userRole: logData.userRole || 'unknown',
      timestamp: new Date().toISOString(),
      createdAt: Timestamp.now()
    };

    // Adicionar campos opcionais apenas se tiverem valor
    if (logData.targetType) log.targetType = logData.targetType;
    if (logData.targetId) log.targetId = logData.targetId;
    if (logData.targetName) log.targetName = logData.targetName;
    if (logData.changes) log.changes = logData.changes;
    if (logData.description) log.description = logData.description;
    if (logData.barbershopId) log.barbershopId = logData.barbershopId;
    if (logData.details) log.details = logData.details;
    log.metadata = {
      ...(logData.metadata || {}),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      platform: typeof navigator !== 'undefined' ? navigator.platform : null,
    };

    // Remover valores undefined recursivamente (Firebase não aceita undefined)
    const cleanedLog = removeUndefined(log);

    const docRef = await addDoc(collection(db, COLLECTION), cleanedLog);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating audit log:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar logs com filtros
 * @param {Object} filters - Filtros opcionais
 * @param {string} filters.userId - Filtrar por usuário
 * @param {string} filters.targetType - Filtrar por tipo de entidade
 * @param {string} filters.targetId - Filtrar por ID da entidade
 * @param {string} filters.action - Filtrar por ação
 * @param {string} filters.startDate - Data inicial (ISO string)
 * @param {string} filters.endDate - Data final (ISO string)
 */
export const getAuditLogs = async (filters = {}) => {
  try {
    let q = collection(db, COLLECTION);
    const constraints = [];

    // Aplicar filtros
    if (filters.userId) {
      constraints.push(where('userId', '==', filters.userId));
    }
    if (filters.targetType) {
      constraints.push(where('targetType', '==', filters.targetType));
    }
    if (filters.targetId) {
      constraints.push(where('targetId', '==', filters.targetId));
    }
    if (filters.action) {
      constraints.push(where('action', '==', filters.action));
    }
    if (filters.startDate) {
      constraints.push(where('timestamp', '>=', filters.startDate));
    }
    if (filters.endDate) {
      constraints.push(where('timestamp', '<=', filters.endDate));
    }

    // Ordenar por data mais recente
    constraints.push(orderBy('timestamp', 'desc'));

    q = query(q, ...constraints);
    const querySnapshot = await getDocs(q);
    
    const logs = [];
    querySnapshot.forEach((doc) => {
      logs.push({ id: doc.id, ...doc.data() });
    });

    return { success: true, data: logs };
  } catch (error) {
    console.error('Error getting audit logs:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar logs de uma barbearia específica
 * @param {string} barbershopId - ID da barbearia
 * @param {string} startDate - Data inicial (opcional)
 * @param {string} endDate - Data final (opcional)
 */
export const getBarbershopLogs = async (barbershopId, startDate = null, endDate = null) => {
  const filters = {
    targetType: 'barbershop',
    targetId: barbershopId,
  };

  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;

  return await getAuditLogs(filters);
};

/**
 * Buscar logs de um usuário específico
 * @param {string} userId - ID do usuário
 * @param {string} startDate - Data inicial (opcional)
 * @param {string} endDate - Data final (opcional)
 */
export const getUserLogs = async (userId, startDate = null, endDate = null) => {
  const filters = {
    userId: userId,
  };

  if (startDate) filters.startDate = startDate;
  if (endDate) filters.endDate = endDate;

  return await getAuditLogs(filters);
};

/**
 * Formatar log para exibição
 */
export const formatLogForDisplay = (log) => {
  return {
    ...log,
    actionLabel: AUDIT_ACTION_LABELS[log.action] || log.action,
    formattedDate: new Date(log.timestamp).toLocaleString('pt-BR'),
  };
};
